import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, LayoutDashboard, LogIn, LogOut, Calculator } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AudienceToggle from './AudienceToggle';
import { MotorInksLogo } from './Brand';
import { TID } from '@/testIds';
import { useAudience } from '@/context/AudienceContext';
import { useAuth } from '@/context/AuthContext';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { isB2B } = useAudience();
  const { isAuthed, isAdmin, user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const onHome = pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const hash = (id) => (onHome ? `#${id}` : `/#${id}`);

  const nav = [
    { label: isB2B ? 'How It Works' : 'Services',
      href: isB2B ? '/how-it-works' : hash('services'),
      internal: isB2B },
    { label: 'Wrapulator', href: hash('wrapulator'), tid: TID.navWrapulator },
    { label: isB2B ? 'Rate Card' : 'Portfolio',
      href: isB2B ? '/rates' : hash('portfolio'),
      tid: isB2B ? TID.navRateCard : TID.navPortfolio, internal: isB2B },
    { label: isB2B ? 'Aftercare' : 'Membership',
      href: isB2B ? '/aftercare' : hash('membership'),
      tid: isB2B ? 'nav-aftercare-link' : TID.navMembership, internal: isB2B },
    { label: isB2B ? 'Partners' : 'Install Guild',
      href: isB2B ? '/partners' : 'https://www.installguild.pro',
      tid: isB2B ? TID.navPartners : TID.navGuildLink,
      internal: isB2B,
      external: !isB2B },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-200 ${
        scrolled ? 'bg-black/90 backdrop-blur-xl border-b mi-line-strong' : 'bg-black/60 backdrop-blur-md border-b mi-line'
      }`}
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 lg:px-8 py-3.5 gap-4">
        <Link to="/" data-testid={TID.navLogo} className="flex items-center gap-3 group flex-shrink-0">
          <MotorInksLogo variant="auto" height={44} className="drop-shadow-[0_2px_8px_rgba(230,57,70,0.35)]" />
        </Link>

        <nav className="hidden lg:flex items-center gap-6 flex-1 justify-center">
          {nav.map((n) => (
            n.internal ? (
              <Link key={n.label} to={n.href} data-testid={n.tid}
                    className="text-sm font-medium tracking-wide text-white/80 hover:text-[#E63946] transition-colors whitespace-nowrap">
                {n.label}
              </Link>
            ) : (
              <a key={n.label} href={n.href} data-testid={n.tid}
                 target={n.external ? '_blank' : undefined}
                 rel={n.external ? 'noopener noreferrer' : undefined}
                 className="text-sm font-medium tracking-wide text-white/80 hover:text-[#E63946] transition-colors whitespace-nowrap">
                {n.label}
              </a>
            )
          ))}
        </nav>

        <div className="flex items-center gap-3 flex-shrink-0">
          <a
            href={hash('wrapulator')}
            data-testid="nav-wrapulator-quicklink"
            title="Open the Wrapulator"
            aria-label="Open the Wrapulator"
            className="group relative inline-flex items-center gap-1.5 border border-[#E63946]/60 bg-[#E63946]/10 text-[#E63946] hover:bg-[#E63946] hover:text-white px-2.5 py-2 rounded-sm transition-colors"
          >
            <Calculator className="w-4 h-4" />
            <span className="hidden xl:inline mi-headline text-[11px] tracking-wider">WRAPULATOR</span>
          </a>
          <div className="hidden md:block"><AudienceToggle compact /></div>
          <a href="tel:8327912692" className="hidden 2xl:flex items-center gap-2 text-[11px] mi-eyebrow text-white/70 hover:text-white transition-colors whitespace-nowrap">
            <Phone className="w-3 h-3" /> 832.791.2692
          </a>

          {isAuthed ? (
            <div className="hidden md:flex items-center gap-2">
              {isAdmin && (
                <Link to="/admin" data-testid={TID.navAdmin}
                      className="mi-btn-ghost text-[11px] py-2 px-3 whitespace-nowrap">
                  <LayoutDashboard className="w-3.5 h-3.5" /> Admin
                </Link>
              )}
              <Link to="/dashboard" data-testid={TID.navDashboard}
                    className="mi-btn-ghost text-[11px] py-2 px-3 whitespace-nowrap">
                <LayoutDashboard className="w-3.5 h-3.5" /> Dashboard
              </Link>
              <button onClick={() => { logout(); navigate('/'); }}
                      className="text-white/50 hover:text-[#E63946] p-2" title={user?.email}>
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            isB2B && (
              <div className="hidden md:flex">
                <Link to="/login" data-testid={TID.navSignIn}
                      className="mi-btn-ghost text-[11px] py-2 px-3 whitespace-nowrap">
                  <LogIn className="w-3.5 h-3.5" /> Sign in
                </Link>
              </div>
            )
          )}

          <div className="hidden md:flex">
            <a
              href={onHome ? '#quote' : '/#quote'}
              data-testid={TID.navQuoteCta}
              className="mi-btn-red text-[12px] py-2.5 px-4 whitespace-nowrap"
            >
              {isB2B ? 'Apply as Partner' : 'Get a Quote'}
            </a>
          </div>
          <button aria-label="menu" className="lg:hidden text-white p-2 border border-white/10 rounded-sm"
                  onClick={() => setOpen(!open)}>
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-black border-t mi-line">
          <div className="flex flex-col p-6 gap-4">
            <div className="pb-2"><AudienceToggle /></div>
            {nav.map((n) => (
              n.internal ? (
                <Link key={n.label} to={n.href} onClick={() => setOpen(false)}
                      className="text-white/85 font-medium py-2 border-b mi-line">
                  {n.label}
                </Link>
              ) : (
                <a key={n.label} href={n.href}
                   target={n.external ? '_blank' : undefined}
                   rel={n.external ? 'noopener noreferrer' : undefined}
                   onClick={() => setOpen(false)}
                   className="text-white/85 font-medium py-2 border-b mi-line">
                  {n.label}
                </a>
              )
            ))}

            {isAuthed ? (
              <>
                {isAdmin && (
                  <Link to="/admin" onClick={() => setOpen(false)}
                        className="text-white/85 font-medium py-2 border-b mi-line inline-flex items-center gap-2">
                    <LayoutDashboard className="w-4 h-4" /> Admin console
                  </Link>
                )}
                <Link to="/dashboard" onClick={() => setOpen(false)}
                      className="text-white/85 font-medium py-2 border-b mi-line inline-flex items-center gap-2">
                  <LayoutDashboard className="w-4 h-4" /> Dashboard
                </Link>
                <button onClick={() => { logout(); setOpen(false); navigate('/'); }}
                        className="text-white/70 hover:text-[#E63946] font-medium py-2 border-b mi-line inline-flex items-center gap-2 text-left">
                  <LogOut className="w-4 h-4" /> Sign out
                </button>
              </>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)}
                    className="text-white/85 font-medium py-2 border-b mi-line inline-flex items-center gap-2">
                <LogIn className="w-4 h-4" /> Sign in
              </Link>
            )}

            <a href={onHome ? '#quote' : '/#quote'} onClick={() => setOpen(false)} className="mi-btn-red mt-2">
              {isB2B ? 'Apply as Partner' : 'Get a Quote'}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
